import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, ExternalLink, FileText, Image as ImageIcon, Loader2, X } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatItalianDate, formatItalianDateTime } from "../lib/dateUtils";

/**
 * Tracciabilità lotto → documento di acquisto.
 * <LotTraceLink lotId="..."> rende cliccabile un numero di lotto ovunque compaia:
 * il clic apre la scheda del lotto con il DDT/fattura caricato al ricevimento.
 */

export const DOCUMENT_STATUS = {
  presente: { text: "Documento allegato", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  da_allegare: { text: "DDT da allegare", cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  mancante: { text: "DDT/fattura mancante", cls: "bg-red-50 text-red-700 ring-red-200" },
};

export function DocumentStatusBadge({ status }) {
  const s = DOCUMENT_STATUS[status] || DOCUMENT_STATUS.mancante;
  const Icon = status === "presente" ? CheckCircle2 : AlertTriangle;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${s.cls}`}><Icon className="h-3.5 w-3.5" />{s.text}</span>;
}

export const documentTitle = (lot) => {
  const r = lot.receipt;
  const label = r?.document_label || "Documento";
  return r?.number ? `${label} n. ${r.number}` : label;
};

export function OfficialDocuments({ lot, compact = false }) {
  const docs = (lot.documents || []).filter((d) => d.role === "accompagnamento");
  if (!docs.length) return null;
  return (
    <div className={`flex flex-col gap-2 ${compact ? "" : "sm:flex-row sm:flex-wrap"}`}>
      {docs.map((d) => d.deleted_at ? (
        <p key={d.url} className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-hairline bg-slate-50 px-4 py-2.5 text-sm text-slate-600"
          data-testid="lot-document-deleted">
          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
          <span>{d.label || "Documento"} eliminato il {formatItalianDate(d.deleted_at)} · conservazione terminata</span>
        </p>
      ) : (
        <a key={d.url} href={d.url} target="_blank" rel="noreferrer"
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-navy px-4 py-2.5 text-sm font-semibold text-white hover:bg-navy-600"
          data-testid="lot-open-document">
          <FileText className="h-4 w-4 shrink-0" />
          <span className="truncate">Apri {documentTitle(lot)}{docs.length > 1 ? ` · ${d.name}` : ""}</span>
          <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </a>
      ))}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[minmax(0,9rem)_1fr] gap-3 border-b border-hairline py-2 text-sm last:border-0">
      <dt className="text-slate-500">{label}</dt><dd className="min-w-0 break-words font-medium text-navy">{children}</dd>
    </div>
  );
}

export function LotDossierDialog({ lotId, onClose }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    api.get(`/app/catalog/raw-lots/${encodeURIComponent(lotId)}/dossier`)
      .then(({ data }) => alive && setState({ lot: data.lot }))
      .catch((e) => alive && setState({ error: formatApiErrorDetail(e.response?.data?.detail) }));
    return () => { alive = false; };
  }, [lotId]);
  useEffect(() => {
    const close = (event) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose]);

  const { lot } = state;
  const photos = (lot?.documents || []).filter((d) => d.role === "foto");
  const official = (lot?.documents || []).filter((d) => d.role === "accompagnamento");
  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-navy/60 backdrop-blur-sm sm:items-center sm:p-4" role="presentation"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="lot-dossier-title"
        className="relative max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-7" data-testid="lot-dossier">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-full text-slate-500 hover:bg-slate-100" aria-label="Chiudi"><X className="h-5 w-5" /></button>
        <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Scheda lotto materia prima</p>
        {state.loading && <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>}
        {state.error && <p className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">{state.error}</p>}
        {lot && (
          <>
            <h2 id="lot-dossier-title" className="mt-1 pr-10 font-heading text-2xl font-bold text-navy">{lot.ingredient_name}</h2>
            <p className="mt-1 font-mono text-lg font-semibold text-gold" data-testid="lot-dossier-code">Lotto {lot.supplier_lot || "—"}</p>

            <section className="mt-4 rounded-xl border border-hairline bg-haccp-bg p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-heading text-base font-bold text-navy">Documento di acquisto</h3>
                <DocumentStatusBadge status={lot.document_status} />
              </div>
              {lot.receipt && (
                <p className="mt-1 text-sm text-slate-600">
                  {documentTitle(lot)} · ricevuto il {formatItalianDate(lot.receipt.received_date)} da {lot.supplier_name}
                  {lot.receipt.lots_in_document === 2 ? " · nello stesso documento c'è un altro lotto" : ""}
                  {lot.receipt.lots_in_document > 2 ? ` · nello stesso documento altri ${lot.receipt.lots_in_document - 1} lotti` : ""}
                </p>
              )}
              <div className="mt-3">
                {official.length > 0 ? (
                  <>
                    <OfficialDocuments lot={lot} />
                    {official.filter((d) => !d.deleted_at && d.retention_until).slice(0, 1).map((d) => (
                      <p key={d.url} className="mt-2 text-xs text-slate-500" data-testid="lot-document-retention">
                        Il PDF si conserva fino al {formatItalianDate(d.retention_until)}, poi viene eliminato in automatico. Lotto, fornitore e scadenza restano registrati.
                      </p>
                    ))}
                  </>
                ) : (
                  <p className="text-sm text-slate-700">
                    {lot.document_status === "da_allegare"
                      ? "Al ricevimento è stato dichiarato che il documento sarebbe stato allegato in seguito: allegalo da Lotti registrati (lista) → Registro ricevimenti merci."
                      : "Per questo lotto non è stato caricato nessun DDT o fattura."}
                    {" "}<Link to="/app/lotti" onClick={onClose} className="font-semibold text-navy underline">Vai ai lotti</Link>
                  </p>
                )}
              </div>
              {official.some((d) => d.sha256) && (
                <p className="mt-3 break-all text-[11px] leading-snug text-slate-400">
                  Impronta SHA-256 registrata al caricamento: {official.filter((d) => d.sha256).map((d) => d.sha256.slice(0, 16)).join(" · ")}…
                </p>
              )}
              {lot.receipt?.document_attached_at && (
                <p className="mt-1 text-xs text-slate-500">Allegato in seguito il {formatItalianDateTime(lot.receipt.document_attached_at)}{lot.receipt.document_attached_by ? ` da ${lot.receipt.document_attached_by}` : ""}</p>
              )}
            </section>

            <dl className="mt-4">
              <Row label="Fornitore">{lot.supplier_name}</Row>
              <Row label="Sede">{lot.site_name || "—"}</Row>
              {lot.product_code && <Row label="Codice prodotto"><span className="font-mono">{lot.product_code}</span></Row>}
              <Row label="Data ricevimento">{formatItalianDate(lot.received_date)}</Row>
              <Row label="Scadenza / TMC">{formatItalianDate(lot.expiry_date)}</Row>
              {lot.quantity != null && <Row label="Quantità">{lot.quantity} {lot.unit}</Row>}
              {lot.receipt && (
                <Row label="Controllo ricevimento">
                  Merce {lot.receipt.outcome || "—"}
                  {lot.receipt.temperature_c != null ? ` · ${lot.receipt.temperature_c} °C` : ""}
                  {lot.receipt.issues?.length ? ` · anomalie: ${lot.receipt.issues.join(", ")}` : ""}
                  {lot.receipt.operator_name ? ` · ${lot.receipt.operator_name}` : ""}
                </Row>
              )}
              <Row label="Stato lotto">{lot.status || "—"}</Row>
            </dl>

            {photos.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-3">
                {photos.map((d) => <a key={d.url} href={d.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-navy underline"><ImageIcon className="h-4 w-4" /> {d.label}{d.deleted_at && <span className="font-normal text-slate-500" data-testid="lot-photo-reduced"> · miniatura (foto intera eliminata il {formatItalianDate(d.deleted_at)})</span>}</a>)}
              </div>
            )}

            <section className="mt-5">
              <h3 className="font-heading text-base font-bold text-navy">Usato in {lot.productions?.length || 0} {lot.productions?.length === 1 ? "produzione" : "produzioni"}</h3>
              {lot.productions?.length > 0 && (
                <ul className="mt-2 divide-y divide-hairline rounded-xl border border-hairline">
                  {lot.productions.map((p) => (
                    <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span><span className="font-mono font-semibold text-navy">{p.lot_code}</span> · {p.recipe_name}{p.via?.length > 0 && <span className="text-xs text-violet-700"> · tramite semilavorato {p.via.join(" → ")}</span>}</span>
                      <span className="text-slate-500">{formatItalianDate(p.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

export function LotTraceLink({ lotId, auto = false, children, className = "" }) {
  const [open, setOpen] = useState(false);
  const real = lotId && !auto && !String(lotId).startsWith("auto:");
  if (!real) return <span className={className} title={auto ? "Lotto automatico giornaliero: nessun documento di acquisto collegato" : undefined}>{children}</span>;
  return (
    <>
      <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 rounded underline decoration-dotted underline-offset-2 hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-navy/40 ${className}`}
        title="Apri il lotto e il suo DDT/fattura" data-testid="lot-trace-link">
        {children}<FileText className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden="true" />
      </button>
      {open && <LotDossierDialog lotId={lotId} onClose={() => setOpen(false)} />}
    </>
  );
}
